import { z } from 'zod'

import { SAFE_PATH } from '@gto/helpers/safe-path'

/**
 * Why the reader is looking at `/login`.
 *
 * `session_ended` means they were signed out mid-session because the admin API
 * rejected their credentials — set by the auth-failure handler in `app.tsx`.
 * `auto_start_failed` means a magic-login handoff asked for an automatic sign-in
 * and the redirect could not be built — set by the login route itself.
 * Its absence means the ordinary case: they simply are not signed in.
 *
 * A CLOSED enum, never free text. The value rides in the URL, so an open string
 * would let a crafted link put arbitrary copy on a sign-in screen — the same
 * threat `from` is regex-validated against rather than trusted.
 */
export const LOGIN_REASONS = ['session_ended', 'auto_start_failed'] as const

export type LoginReason = (typeof LOGIN_REASONS)[number]

/**
 * `/login`'s view state. Lives beside the page rather than inside `routes/` so
 * the route declaration and its tests read the one definition — same reason as
 * `announcements-search.ts`.
 *
 * Both fields `.catch(undefined)`: an unparseable value degrades to the plain
 * unauthenticated screen, which is always a safe thing to show. Throwing would
 * turn a mangled link into an error page on the one route that exists to get
 * people back in.
 */
export const loginSearchSchema = z.object({
  from: z.string().regex(SAFE_PATH).optional().catch(undefined),
  reason: z.enum(LOGIN_REASONS).optional().catch(undefined),
  auto: z
    .union([z.literal('1'), z.literal(1)])
    .optional()
    .catch(undefined),
})

export type LoginCopy = {
  heading: string
  body: string
}

const UNAUTHENTICATED_COPY: LoginCopy = {
  heading: 'Admin sign-in required',
  body: "Sign in with your GTO Wizard work account. You'll be redirected to the main app briefly, then back to admin.",
}

const REASON_COPY: Record<LoginReason, LoginCopy> = {
  // Deliberately warns that retrying may not help. The most likely cause is an
  // account without admin permissions, and the failure mode this replaces was a
  // silent bounce that invited an endless sign-in loop.
  session_ended: {
    heading: 'Your admin session ended',
    body: 'The admin API rejected this account, so you were signed out. Signing in again with the same account will fail the same way — if that happens, it probably has no admin permissions.',
  },
  auto_start_failed: {
    heading: 'Automatic sign-in could not start',
    body: 'The automatic sign-in could not build its redirect. Sign in manually to continue. The manual start needs the same browser features, so it can fail the same way.',
  },
}

/** The copy `/login` should show, given why the reader arrived. */
export const loginCopyFor = (reason: LoginReason | undefined): LoginCopy =>
  reason === undefined ? UNAUTHENTICATED_COPY : REASON_COPY[reason]
